import { useMemo, useRef } from 'react';
import {
  Card,
  CardBody,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import {
  PROGRAM_UNIT_LABELS,
  PROGRAM_UNIT_SHORT,
  buildCommittedMatrix,
  buildShippedMatrix,
  commitmentsAt,
  computeCompliance,
  formatProgramQuantity,
} from 'common/src/utils/commercialProgram';
import type {
  CommercialProgram,
  CommercialProgramVersion,
  ExporterReceiver,
} from 'common/src/utils/types';
import ChartDownloadButton from 'common/src/components/charts/ChartDownloadButton';
import { useTranslation } from 'common/src/hooks/useTranslation';
import CommitmentsChart from '../../components/charts/CommitmentsChart';
import { Alert, EmptyState, StatusChip } from '../../components/ui';
import type { CommercialProgramState } from './useCommercialProgram';

interface SummaryViewProps {
  state: CommercialProgramState;
  program: CommercialProgram;
  receivers: ExporterReceiver[];
  /** Versión elegida arriba; null = programa vigente. */
  version: CommercialProgramVersion | null;
}

const complianceColor = (pct: number | null) =>
  pct == null
    ? 'default'
    : pct >= 90
      ? 'success'
      : pct >= 75
        ? 'warning'
        : 'danger';

const formatPct = (pct: number | null) =>
  pct == null
    ? '–'
    : `${pct.toLocaleString('es-CL', { maximumFractionDigits: 1 })}%`;

/**
 * Cumplimiento del programa: a la fecha y sobre el total, contra la versión
 * vigente o una versión guardada.
 */
export default function SummaryView({
  state,
  program,
  receivers,
  version,
}: SummaryViewProps) {
  const { t } = useTranslation('commitments');
  const { rows, shipments, receiverIdOf, weeks, cutoffIndex } = state;
  const chartRef = useRef<HTMLDivElement>(null);

  const unit = program.unit;
  const receiverIds = useMemo(
    () => new Set(receivers.map((r) => r.id)),
    [receivers]
  );
  // Contra una versión, las semanas se cuentan con la fecha base que tenía.
  const dateBasis = version?.date_basis ?? program.date_basis;

  const committed = useMemo(
    () =>
      buildCommittedMatrix(commitmentsAt(rows, version?.as_of), program, unit),
    [rows, version, program, unit]
  );

  // Despachado por cliente y semana, según la fecha base del programa.
  const { shipped, withoutDate, outsideSeason } = useMemo(
    () =>
      buildShippedMatrix({
        shipments,
        receiverIdOf,
        receiverIds,
        weeks,
        unit,
        dateBasis,
        equivalences: program,
      }),
    [shipments, receiverIdOf, receiverIds, unit, program, weeks, dateBasis]
  );

  const sumWeeks = (
    matrix: Map<number, Map<number, number>>,
    receiverId?: number
  ) =>
    weeks.map((w) =>
      (receiverId != null ? [receiverId] : Array.from(receiverIds)).reduce(
        (sum, id) => sum + (matrix.get(id)?.get(w) || 0),
        0
      )
    );

  const committedByWeek = sumWeeks(committed);
  const shippedByWeek = sumWeeks(shipped);
  const summary = computeCompliance(
    committedByWeek,
    shippedByWeek,
    cutoffIndex
  );

  const clientRows = receivers
    .map((r) => ({
      receiver: r,
      ...computeCompliance(
        sumWeeks(committed, r.id),
        sumWeeks(shipped, r.id),
        cutoffIndex
      ),
    }))
    .filter((row) => row.totalCommitted > 0 || row.totalShipped > 0)
    .sort((a, b) => b.totalCommitted - a.totalCommitted);

  const fmt = (v: number) => formatProgramQuantity(v, unit);
  const unitLabel = t(PROGRAM_UNIT_LABELS[unit]);
  const cutoffWeek =
    cutoffIndex >= 0 ? weeks[Math.min(cutoffIndex, weeks.length - 1)] : null;

  const unitShort = PROGRAM_UNIT_SHORT[unit];

  // Indicador con el formato de KpiCard, más la barra de avance.
  const complianceCard = (
    title: string,
    subtitle: string,
    shippedValue: number,
    committedValue: number,
    pct: number | null
  ) => (
    <Card shadow='sm'>
      <CardBody className='gap-1 p-6'>
        <div className='flex items-baseline justify-between gap-2'>
          <span className='text-xs uppercase tracking-wide text-default-500'>
            {title}
          </span>
          <span className='text-xs text-default-400'>{subtitle}</span>
        </div>
        <span className='text-2xl font-semibold'>{formatPct(pct)}</span>
        <span className='text-xs text-default-500'>
          {t('{{shipped}} de {{committed}} {{unit}} despachados', {
            shipped: fmt(shippedValue),
            committed: fmt(committedValue),
            unit: unitShort,
          })}
        </span>
        <Progress
          aria-label={title}
          size='sm'
          className='mt-2'
          value={Math.min(pct ?? 0, 100)}
          color={complianceColor(pct)}
        />
      </CardBody>
    </Card>
  );

  return (
    <div className='flex flex-col gap-4'>
      {withoutDate > 0 && (
        <Alert variant='warning' showIcon>
          {t('{{amount}} {{unit}} despachados no tienen fecha de {{basis}}.', {
            amount: fmt(withoutDate),
            unit: PROGRAM_UNIT_SHORT[unit],
            basis: dateBasis === 'eta' ? t('arribo') : t('despacho'),
          })}
        </Alert>
      )}
      {outsideSeason > 0 && (
        <Alert variant='warning' showIcon>
          {t(
            '{{amount}} {{unit}} despachados caen fuera de las semanas de la temporada.',
            {
              amount: fmt(outsideSeason),
              unit: PROGRAM_UNIT_SHORT[unit],
            }
          )}
        </Alert>
      )}

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        {complianceCard(
          t('A la fecha'),
          cutoffWeek != null
            ? t('Hasta la semana {{week}}', { week: cutoffWeek })
            : t('La temporada aún no empieza'),
          summary.shippedToDate,
          summary.committedToDate,
          summary.toDatePct
        )}
        {complianceCard(
          t('Sobre el total de la temporada'),
          t('Todo lo comprometido'),
          summary.totalShipped,
          summary.totalCommitted,
          summary.totalPct
        )}
      </div>

      <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
        <Card shadow='sm'>
          <CardBody className='gap-4 p-6'>
            <div className='flex items-center gap-2'>
              <h3 className='text-lg font-semibold'>
                {t('Evolución semanal')}
              </h3>
              <ChartDownloadButton
                chartRef={chartRef}
                title={t('Evolución semanal')}
              />
            </div>
            <div ref={chartRef}>
              <CommitmentsChart
                data={weeks.map((w, i) => ({
                  week: w,
                  comprometido: committedByWeek[i],
                  enviados: shippedByWeek[i],
                }))}
                unitLabel={unitLabel}
                unitShort={PROGRAM_UNIT_SHORT[unit]}
                decimals={unit === 'shipment' ? 2 : 0}
              />
            </div>
          </CardBody>
        </Card>

        <Card shadow='sm'>
          <CardBody className='gap-4 p-6'>
            <div>
              <h3 className='text-lg font-semibold'>
                {t('Resumen por cliente')}
              </h3>
              <p className='text-sm text-default-500'>
                {t('En {{unit}}', { unit: unitLabel.toLowerCase() })}
              </p>
            </div>
            <Table
              aria-label={t('Resumen por cliente')}
              removeWrapper
              isHeaderSticky
              classNames={{ base: 'max-h-[460px] overflow-y-auto' }}
            >
              <TableHeader>
                <TableColumn>{t('CLIENTE')}</TableColumn>
                <TableColumn align='center'>{t('A LA FECHA')}</TableColumn>
                <TableColumn align='end'>{t('DEL TOTAL')}</TableColumn>
              </TableHeader>
              <TableBody
                emptyContent={
                  <EmptyState
                    title={t('Todavía no hay compromisos cargados.')}
                  />
                }
              >
                {clientRows.map((row) => (
                  <TableRow key={row.receiver.id}>
                    <TableCell
                      className='max-w-[180px] truncate font-medium'
                      title={row.receiver.name}
                    >
                      {row.receiver.name}
                    </TableCell>
                    {/* Cada % con lo despachado / comprometido de donde sale. */}
                    <TableCell className='text-center'>
                      <StatusChip tone={complianceColor(row.toDatePct)}>
                        {formatPct(row.toDatePct)}
                      </StatusChip>
                      <p className='mt-0.5 whitespace-nowrap text-xs text-default-400'>
                        {fmt(row.shippedToDate)} / {fmt(row.committedToDate)}
                      </p>
                    </TableCell>
                    <TableCell className='text-right'>
                      <p className='text-default-500'>
                        {formatPct(row.totalPct)}
                      </p>
                      <p className='mt-0.5 whitespace-nowrap text-xs text-default-400'>
                        {fmt(row.totalShipped)} / {fmt(row.totalCommitted)}
                      </p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
